import React, { memo } from 'react';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import { Image } from 'components';
import { Row, Col } from 'components/layout';
import { Input } from 'components/form';
import { GamificationProps } from 'pages/company/Gamification';
import {
  StudentSocietyRequest,
  StudentSocietyResponse,
} from 'lib/models/student-society';

import { REGEX } from 'lib/utils/regex';
import SectionWrapper from '../SectionWrapper';

import styles from './SocialLinks.module.scss';

export interface SocialLinksProps extends GamificationProps {
  /** Custom class name */
  className?: string;
  /** instagram url */
  instagram_url: string;
  /** facebook url */
  facebook_url: string;
  /** linkedin url */
  linkedin_url: string;
  /** website url */
  website: string;
  /** student union url */
  student_union_url: string;
  /** On society profile update click handler */
  onUpdate: (
    data: StudentSocietyRequest
  ) => Promise<StudentSocietyResponse | undefined>;
  showProgressBanner: boolean;
}

export const SocialLinks: React.FC<SocialLinksProps> = memo(
  ({
    className = '', // custom class name
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    showProgressBanner,
  }: SocialLinksProps) => {
    const contentClassNames = cn(
      {
        [styles.SocietyProfileDetailsForm]: true,
      },
      className
    );

    const {
      register,
      formState: { errors },
    } = useFormContext();

    return (
      <SectionWrapper
        title="Social media links"
        description="Add your social media links to get more traffic."
        progressDetails={progressDetails}
        profileCompletePercent={profileCompletePercent}
        nonCompletedSection={nonCompletedSection}
        handleProfileCompletionButton={handleProfileCompletionButton}
        handleNextButton={handleNextButton}
        showNextButton={showNextButton}
        showProgressBanner={showProgressBanner}
      >
        <div className={contentClassNames}>
          <Row className={styles.SocietyProfileDetailsFormRow} gutter={50}>
            <Col>
              <div
                className={styles.SocietyProfileDetailsFormSocialLinkContainer}
              >
                <Row
                  className={
                    styles.SocietyProfileDetailsFormSocialLinkContainerRow
                  }
                >
                  <Col
                    span={24}
                    className={styles.SocietyProfileDetailsFormSocialLink}
                  >
                    <Image src="/images/instagram.png" alt="instagram" />
                    <Input
                      placeholder="Add your Instagram link"
                      className={
                        styles.SocietyProfileDetailsFormSocialLinkInput
                      }
                      {...register('instagram_url', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.instagram_url}
                      helperText={errors.instagram_url?.message}
                    />
                  </Col>
                  <Col
                    span={24}
                    className={styles.SocietyProfileDetailsFormSocialLink}
                  >
                    <Image src="/images/facebook.png" alt="facebook" />
                    <Input
                      placeholder="Add your Facebook link"
                      className={
                        styles.SocietyProfileDetailsFormSocialLinkInput
                      }
                      {...register('facebook_url', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.facebook_url}
                      helperText={errors.facebook_url?.message}
                    />
                  </Col>
                  <Col
                    span={24}
                    className={styles.SocietyProfileDetailsFormSocialLink}
                  >
                    <Image src="/images/linkedin.png" alt="linkedin" />
                    <Input
                      placeholder="Add your LinkedIn link"
                      className={
                        styles.SocietyProfileDetailsFormSocialLinkInput
                      }
                      {...register('linkedin_url', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.linkedin_url}
                      helperText={errors.linkedin_url?.message}
                    />
                  </Col>
                  <Col
                    span={24}
                    className={styles.SocietyProfileDetailsFormSocialLink}
                  >
                    <Image src="/images/web-link.png" alt="web link" />
                    <Input
                      placeholder="Add your Website link"
                      className={
                        styles.SocietyProfileDetailsFormSocialLinkInput
                      }
                      {...register('website', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.website}
                      helperText={errors.website?.message}
                    />
                  </Col>
                  <Col
                    span={24}
                    className={styles.SocietyProfileDetailsFormSocialLink}
                  >
                    <Image
                      src="/images/student-union.png"
                      alt="student union"
                    />
                    <Input
                      placeholder="Add your Student union link"
                      className={
                        styles.SocietyProfileDetailsFormSocialLinkInput
                      }
                      {...register('student_union_url', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.student_union_url}
                      helperText={errors.student_union_url?.message}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </SectionWrapper>
    );
  }
);

SocialLinks.displayName = 'SocialLinks';
